
import { useStore } from '@/store'
import * as THREE from 'three'
import { defineComponent, inject, onUnmounted } from 'vue'
import { computed, watch } from 'vue'
import MaterialCreateService from '@/services/Three/material-create.service'
import References from '@/singletons/references'
import tackSettings from '@/settings/tack-settings.json'
import TextureLoadService from '@/services/Three/texture-load.service'
import SentrySingleton from '@/singletons/sentry-singleton'
import { tackColorShaderString } from '@/shaders/tackColor'

export default defineComponent({
  name: 'ThreeRandselTack',
  setup() {
    const store = useStore()
    const emitter: any = inject('emitter')
    const isBagLoaded = computed(() => store.getters.isBagLoaded)
    const isSceneLoaded = computed(() => store.getters.isSceneLoaded)
    const configTackId = computed(() => store.getters.configTackId)
    const colorpickerTack = computed(() => store.getters.colorpickerTack)
    const materialCreateService: MaterialCreateService = new MaterialCreateService()
    let textureLoadService: TextureLoadService
    let material: THREE.MeshPhongMaterial

    emitter.on('updateConfig', updateOption)

    // prettier-ignore
    watch(() => isSceneLoaded.value, (_) => {
      try {
        initTextureLoadService()
        material = new THREE.MeshPhongMaterial()
        material.color = materialCreateService.createColor(0xFFFFFF)
        material.specular = materialCreateService.createColor(0xFFFFFF)
        material.shininess = 30
        material.dithering = true
        material['skinning'] = true
        textureLoadService.loadImage('default/covertack', (tex: THREE.Texture | null) => {
          tex!.encoding = THREE.sRGBEncoding;
          material.map = tex
          material.needsUpdate = true
        })
        createMaterial2();
      }
      catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    }
    )

    function initTextureLoadService() {
      if (!textureLoadService || textureLoadService == undefined) {
        textureLoadService = new TextureLoadService(References.instance.getSceneReferences().maxAnisotropy)
      }
    }

    function createMaterial2() {
      let mat = materialCreateService.createMaterial(tackColorShaderString, false, true, false, true)
      mat.uniforms.diffuse.value = materialCreateService.createColor('#FFFFFF')
      mat.uniforms.texture = { value: null }
      mat.uniforms.normalScale.value = materialCreateService.getNormalMapScale()
      mat.uniforms.roughness.value = materialCreateService.getRoughness()
      mat.uniforms.metalness.value = materialCreateService.getMetalness()
      References.instance.setCovertackColorMaterial(mat)
      textureLoadService.loadImage('default/covertack_color', (tex: THREE.Texture | null) => {
        tex!.encoding = THREE.sRGBEncoding;
        References.instance.getCovertackColorMaterial().uniforms.texture.value = tex
        References.instance.getCovertackColorMaterial().needsUpdate = true
      })
      textureLoadService.loadImage('default/covertack_color_alpha', (tex: THREE.Texture | null) => {
        tex!.encoding = THREE.sRGBEncoding;
        References.instance.getCovertackColorMaterial().uniforms.alphaMap.value = tex
        References.instance.getCovertackColorMaterial().needsUpdate = true
      })
      // textureLoadService.loadImage('default/covertack_color_clip', (tex: THREE.Texture | null) => {
      //   tex!.encoding = THREE.sRGBEncoding;
      //   References.instance.getCovertackColorMaterial().uniforms.texture.value = tex
      //   References.instance.getCovertackColorMaterial().needsUpdate = true
      // })
    }

    // prettier-ignore
    watch(() => isBagLoaded.value, (_) => {
      try {
        References.instance.getBagReferences().covertacks.forEach(tack => {
          const option = tackSettings.options.find(x => x.value == tack.name);
          if (option && option.useTackMaterial2) {
            tack.material = References.instance.getCovertackColorMaterial()
            tack.receiveShadow = false
          }
          else {
            tack.material = material
          }
        })
      }
      catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    })
    // prettier-ignore
    watch(() => configTackId.value, (_) => { updateOption() })
    // prettier-ignore
    watch(() => colorpickerTack.value, (_) => { updateColorpicker() })

    function updateOption() {
      try {
        if (configTackId.value) {
          setTack(tackSettings.options.find((x: any) => x.id == configTackId.value))
        } else {
          setTack(tackSettings.options.find((x: any) => x.default && x.default === true))
        }
      } catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    }

    function setTack(obj: any) {
      References.instance.getBagReferences().covertacks.forEach((tack) => {
        tack.visible = tack.name === obj.value
      })
    }

    function updateColorpicker() {
      let color = colorpickerTack.value ? materialCreateService.createColor(colorpickerTack.value) : null

      References.instance.getCovertackColorMaterial().uniforms.diffuse.value = color
    }

    onUnmounted(() => {
      emitter.off('updateConfig', updateOption)
    })

    return {
      isBagLoaded,
      isSceneLoaded,
      configTackId,
      colorpickerTack
    }
  }
})
